/**
 * ServicePackageStep
 * Step 1: Select Service & Package
 */

import { useState, useMemo } from "react";
import { FiPackage, FiRepeat } from "react-icons/fi";
import Button from "@/components/ui/Button";
import EnhancedPackageCard from "@/components/booking/EnhancedPackageCard";
import PackageComparison from "@/components/booking/PackageComparison";
import VisualPackageBuilder from "@/components/booking/VisualPackageBuilder";
import { formatCurrency } from "@/utils/format";
import { PackageConfig, Addon, Service, BookingFormData } from "@/types";
import { BookingFormErrors } from "../types";

interface ServicePackageStepProps {
  formData: Partial<BookingFormData>;
  errors: BookingFormErrors;
  services: Service[];
  packages: PackageConfig[];
  addons: Addon[];
  selectedPackage: PackageConfig | null;
  selectedAddons: Addon[];
  subtotalAmount: number;
  totalAmount: number;
  minServicePriceById: Map<string, number>;
  onFormChange: (updates: Partial<BookingFormData>) => void;
  onAddonToggle: (addonId: string) => void;
  onNext: () => void;
}

export default function ServicePackageStep({
  formData,
  errors,
  services,
  packages,
  addons,
  selectedPackage,
  selectedAddons,
  subtotalAmount,
  totalAmount,
  minServicePriceById,
  onFormChange,
  onAddonToggle,
  onNext,
}: ServicePackageStepProps) {
  const [showComparison, setShowComparison] = useState(false);

  // Sort services by minimum price
  const servicesSortedByPrice = useMemo(() => {
    const decorated = services.map((service) => ({
      service,
      minPrice: minServicePriceById.get(service.id),
    }));

    decorated.sort((a, b) => {
      const aPrice = a.minPrice ?? Number.POSITIVE_INFINITY;
      const bPrice = b.minPrice ?? Number.POSITIVE_INFINITY;
      if (aPrice !== bPrice) return aPrice - bPrice;
      return a.service.order - b.service.order;
    });

    return decorated.map((x) => x.service);
  }, [services, minServicePriceById]);

  // Get packages for selected service
  const availablePackages = useMemo(() => {
    if (!formData.serviceId) return [];
    return packages
      .filter((pkg) => pkg.serviceId === formData.serviceId && pkg.enabled)
      .sort((a, b) => {
        if (a.price !== b.price) return a.price - b.price;
        if (a.order !== b.order) return a.order - b.order;
        return a.name.localeCompare(b.name);
      });
  }, [formData.serviceId, packages]);

  // Get optional addons for selected package
  const availableAddons = useMemo(() => {
    if (!selectedPackage) return [];
    return selectedPackage.optionalAddons
      .map((addonId) => addons.find((a) => a.id === addonId))
      .filter((addon): addon is Addon => Boolean(addon))
      .sort((a, b) => a.price - b.price);
  }, [selectedPackage, addons]);

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-neutral-900 dark:text-white flex items-center gap-2">
            <FiPackage className="text-primary-600" />
            Select Service & Package
          </h2>
          <div className="flex gap-2">
            {formData.serviceId && availablePackages.length > 1 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowComparison(true)}
                className="hidden sm:flex items-center gap-2"
              >
                <FiRepeat size={16} />
                Compare
              </Button>
            )}
          </div>
        </div>

        {/* Service Selection */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            Select Service *
          </label>
          <select
            className={`w-full px-4 py-2 border rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-900 dark:text-white ${
              errors.serviceId ? "border-red-500" : "border-neutral-300 dark:border-neutral-700"
            }`}
            value={formData.serviceId || ""}
            onChange={(e) => {
              onFormChange({
                serviceId: e.target.value,
                packageId: undefined,
              });
            }}
          >
            <option value="">Choose a service</option>
            {servicesSortedByPrice.map((service) => {
              const minPrice = minServicePriceById.get(service.id);
              const label =
                minPrice === undefined
                  ? service.title
                  : `${service.title} (from ${formatCurrency(minPrice)})`;

              return (
                <option key={service.id} value={service.id}>
                  {label}
                </option>
              );
            })}
          </select>
          {errors.serviceId && <p className="mt-1 text-sm text-red-600">{errors.serviceId}</p>}
        </div>

        {/* Package Selection */}
        {formData.serviceId && availablePackages.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
              Select Package *
            </label>
            <div className="grid sm:grid-cols-2 gap-3 max-h-[600px] overflow-y-auto pr-2 border border-neutral-200 dark:border-neutral-700 rounded-lg p-3 bg-neutral-50 dark:bg-neutral-900/40">
              {availablePackages.map((pkg) => {
                const includedAddons = addons.filter((addon) =>
                  pkg.includedAddons.includes(addon.id)
                );
                return (
                  <EnhancedPackageCard
                    key={pkg.id}
                    package={pkg}
                    includedAddons={includedAddons}
                    isSelected={formData.packageId === pkg.id}
                    onSelect={() => onFormChange({ packageId: pkg.id })}
                  />
                );
              })}
            </div>
            {errors.packageId && <p className="text-red-600 text-sm mt-2">{errors.packageId}</p>}
          </div>
        )}

        {/* Optional Addons */}
        {selectedPackage && availableAddons.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
              Additional Add-ons (Optional)
            </label>
            <div className="grid sm:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-2 border border-neutral-200 dark:border-neutral-700 rounded-lg p-3 bg-neutral-50 dark:bg-neutral-900/40">
              {availableAddons.map((addon) => {
                const isSelected = formData.addonIds?.includes(addon.id);

                return (
                  <div
                    key={addon.id}
                    onClick={() => onAddonToggle(addon.id)}
                    className={`p-3 border-2 rounded-lg cursor-pointer transition ${
                      isSelected
                        ? "border-primary-600 bg-primary-50 dark:bg-primary-900/20"
                        : "border-neutral-200 dark:border-neutral-800 hover:border-primary-300"
                    }`}
                  >
                    <div className="flex flex-col gap-1">
                      <div className="flex justify-between items-start gap-2">
                        <h4 className="font-medium text-neutral-900 dark:text-white text-sm">
                          {addon.title}
                        </h4>
                        <span className="font-semibold text-primary-600 shrink-0 text-sm">
                          +{formatCurrency(addon.price)}
                        </span>
                      </div>
                      <p className="text-xs text-neutral-600 dark:text-neutral-300">
                        {addon.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-end">
          <Button onClick={onNext} disabled={!formData.packageId}>
            Next
          </Button>
        </div>
      </div>

      {/* Summary Sidebar */}
      <div className="lg:col-span-1">
        <VisualPackageBuilder
          selectedPackage={selectedPackage}
          selectedAddons={selectedAddons}
          includedAddons={
            selectedPackage
              ? addons.filter((addon) => selectedPackage.includedAddons.includes(addon.id))
              : []
          }
          subtotal={subtotalAmount}
          total={totalAmount}
        />
      </div>

      {/* Package Comparison Modal */}
      {showComparison && formData.serviceId && (
        <PackageComparison
          packages={availablePackages}
          addons={addons}
          selectedPackageId={formData.packageId}
          onSelectPackage={(pkgId: string) => {
            onFormChange({ packageId: pkgId });
            setShowComparison(false);
          }}
          onClose={() => setShowComparison(false)}
        />
      )}
    </div>
  );
}
